import type { EntradasDoAno } from "@base/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { mesesSemPlanejamento, metaDoPeriodo, type MetaDoPeriodo } from "../domain/meta-do-periodo";
import type { FunilProjetadoGateway } from "../infrastructure/funil-projetado.gateway";

const Contexto = createContext<FunilProjetadoGateway | null>(null);

export const chavesDoFunilProjetado = {
  anos: (clienteId: string) => ["funil-projetado", clienteId, "anos"] as const,
  realizado: (clienteId: string, ano: number) => ["funil-projetado", clienteId, "realizado", ano] as const,
};

export function ProvedorDeFunilProjetado({
  gateway,
  children,
}: {
  readonly gateway: FunilProjetadoGateway;
  readonly children: ReactNode;
}) {
  return <Contexto.Provider value={gateway}>{children}</Contexto.Provider>;
}

function useGateway(): FunilProjetadoGateway {
  const gateway = useContext(Contexto);
  if (!gateway) throw new Error("useGateway precisa estar dentro de <ProvedorDeFunilProjetado>.");
  return gateway;
}

export function useAnosDoFunil(clienteId: string) {
  const gateway = useGateway();
  return useQuery({ queryKey: chavesDoFunilProjetado.anos(clienteId), queryFn: () => gateway.anos(clienteId) });
}

export function useRealizadoDoAno(clienteId: string, ano: number | null) {
  const gateway = useGateway();
  return useQuery({
    queryKey: chavesDoFunilProjetado.realizado(clienteId, ano ?? 0),
    queryFn: () => gateway.realizado(clienteId, ano as number),
    enabled: ano !== null,
  });
}

function useAtualizarAnos(clienteId: string) {
  const cliente = useQueryClient();
  return () => cliente.invalidateQueries({ queryKey: chavesDoFunilProjetado.anos(clienteId) });
}

export function useCriarAno(clienteId: string) {
  const gateway = useGateway();
  const atualizar = useAtualizarAnos(clienteId);
  return useMutation({
    mutationFn: ({ ano, copiarDe }: { ano: number; copiarDe?: number | undefined }) =>
      gateway.criarAno(clienteId, ano, copiarDe),
    onSuccess: () => atualizar(),
  });
}

export function useSalvarAno(clienteId: string) {
  const gateway = useGateway();
  const atualizar = useAtualizarAnos(clienteId);
  return useMutation({
    mutationFn: ({ ano, entradas }: { ano: number; entradas: EntradasDoAno }) =>
      gateway.salvarAno(clienteId, ano, entradas),
    onSuccess: () => atualizar(),
  });
}

export function useRemoverAno(clienteId: string) {
  const gateway = useGateway();
  const atualizar = useAtualizarAnos(clienteId);
  return useMutation({ mutationFn: (ano: number) => gateway.removerAno(clienteId, ano), onSuccess: () => atualizar() });
}

export type MetaDoPeriodoNoPainel = {
  /** `null` quando algum mes do periodo nao foi planejado (meta parcial enganaria). */
  readonly meta: MetaDoPeriodo | null;
  /** Meses sem Funil Projetado, para avisar ("outubro/2026"). */
  readonly mesesSemPlanejamento: readonly string[];
  readonly carregando: boolean;
};

/** Meta do periodo do Dashboard e os meses que faltam planejar. */
export function useMetaDoPeriodo(clienteId: string, inicio: Date, fim: Date): MetaDoPeriodoNoPainel {
  const anos = useAnosDoFunil(clienteId);
  return useMemo(() => {
    if (!anos.data) return { meta: null, mesesSemPlanejamento: [], carregando: anos.isPending };
    const porAno = new Map(anos.data.map((funil) => [funil.ano, funil]));
    const faltando = mesesSemPlanejamento(porAno, inicio, fim);
    return {
      meta: faltando.length > 0 ? null : metaDoPeriodo(porAno, inicio, fim),
      mesesSemPlanejamento: faltando,
      carregando: false,
    };
  }, [anos.data, anos.isPending, inicio, fim]);
}
